import { useState, useEffect, useRef } from "react";
import { Container, Grid, Select, MenuItem, InputLabel, FormControl, Tooltip } from "@mui/material";
import QLIReportList from "./QLIReportList";
import _ from "lodash";
import Snackbar from "../../../components/Snackbar";
import { GetColumns } from "./Constants";
import { commonStrings } from "../../../components/StringBundle";
import { generateFilterPayload, transformData } from "../../../components/FilterUtils";
import { getTableData, getqlireportviewpicklist } from "./Services";
import { exportToExcel, company_name_list, isCompanyRestrictedRole, getCompanyFilterList, getDefaultCompany} from "../../common";
import Fab from "../../../components/Fab";
import { roles } from '../../../constants/roles';
import useToken from '../../../hooks/useToken';
import { hideExportExcel } from "../../../utils/utility";
import { useGlobalStyles } from "../../../components/globalStyles";

const { categories } = GetColumns();

const QLIReports = () => {
  const globalClasses = useGlobalStyles();
  const initialCategory = getInitialCategory(categories);
  const { getCurrentUserDetails } = useToken();
  const currentUserDetails = getCurrentUserDetails();
  const role = currentUserDetails?.role;

  const [showSnack, setSnack] = useState({ open: false, message: "", severity: "" });
  const [status, setStatus] = useState([]);
  const [pickListValue, setPickListValue] = useState();
  const [category, setCategory] = useState(initialCategory);
  const [company, setCompany] = useState(getDefaultCompany(role, "qliCompany"));
  const [isExportHidden, setIsExportHidden] = useState(false);

  useEffect(() => {
    const hidden = hideExportExcel(role, roles);
    setIsExportHidden(hidden);
  }, [role]);

  const qliListRef = useRef(null);

  // Get initial category from sessionStorage or default to first item
  function getInitialCategory(categories) {
    return sessionStorage.getItem('selectedQliCategory') || categories[0]?.label;
  }

  // Get initial status from sessionStorage or default to first item
  useEffect(() => {
    getqlireportviewpicklist().then((res) => {
      setStatus(res);
      const storedView = sessionStorage.getItem('qliviewpicklist');
      if (storedView) {
        setPickListValue(storedView);
      } else if (res.length > 0) {
        setPickListValue(res[0].lov_name);
        sessionStorage.setItem('qliviewpicklist', res[0].lov_name);
      }
    });
  }, []);

  const formatToSelection = (data = [], key, id) => {
    let formattedData = [];
    data.map((v) => formattedData.push({ label: v[key], value: v[id] }));
    return formattedData;
  };

  // status dropdown function
  const onHandleStatusChange = (event) => {
    setPickListValue(event.target.value);
    sessionStorage.setItem('qliviewpicklist', event.target.value);
    sessionStorage.setItem('selectedpagenumber', 1);
    sessionStorage.setItem('FiltersTable./QLI-Reports', JSON.stringify([]));
    if (qliListRef.current) {
      qliListRef.current.reset(); //  Reset child table filters
    }
  };

  //category dropdown function
  const handleChangecategory = (event) => {
    const value = event.target.value;
    setCategory(value);
    sessionStorage.setItem('selectedQliCategory', value);
    sessionStorage.setItem('selectedpagenumber', 1);
    if (qliListRef.current) qliListRef.current.resetPage();
  };

  const handleCompanyChange = (event) => {
    setCompany(event.target.value);
    sessionStorage.setItem("qliCompany", event.target.value);
    sessionStorage.setItem("selectedpagenumber", 1);
    if (qliListRef.current) qliListRef.current.resetPage();
  };

  // This function will be triggered by the export button
  const handleExportData = async () => {
    if (qliListRef.current) {
      const { totalrecord, filters } = qliListRef.current.getExportParams();
      const updatedSearchValue = [...filters];
      const existingFilter = updatedSearchValue.find(v => v.searchedColumn === 'quote_category');

      if (existingFilter) {
        existingFilter.updatedSearchValue = category;
      } else {
        updatedSearchValue.push({
          searchText: category,
          searchedColumn: 'quote_category',
          searchedTitle: 'Quote Category'
        });
      }

      const payload = transformData(generateFilterPayload(updatedSearchValue, {
        request_type: pickListValue,
        page: 1,
        limit: totalrecord,
        company_name: company
      }));

      try {
        const response = await getTableData(payload);
        const data = response?.report_details || [];

        if (!data.length) {
          setSnack({
            open: true,
            message: commonStrings.noDataToExport,
            severity: commonStrings.error,
          });
          return;
        }

        exportToExcel(data, "PackingType");
      } catch (e) {
        setSnack({
          open: true,
          message: commonStrings.noDataToExport,
          severity: commonStrings.error,
        });
      }
    }
  };

  return (
    <Container className={globalClasses.sampleroot}>
      <>
        {showSnack.open && <Snackbar {...showSnack} handleClose={() => setSnack({ open: false, message: '', severity: '' })} />}
        <Grid container spacing={1} className={globalClasses.margin10}>
          <Grid xs={4} sm={2} md={1} item>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="company-picklist-label">{commonStrings.company}</InputLabel>
              <Select
                value={company}
                labelId="company-picklist-label"
                onChange={handleCompanyChange}
                label={commonStrings.company}
                id="company-picklist-select"
                disabled={isCompanyRestrictedRole(role)}
                MenuProps={{
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  getContentAnchorEl: null
                }}
              >
                {getCompanyFilterList(role).map((item, index) => (
                  <MenuItem value={item?.value} key={index}>
                    {item?.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={8} sm={3} md={2} item>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="status-label">{commonStrings.pickList}</InputLabel>
              <Select
                value={pickListValue || ''}
                labelId="status-label"
                onChange={onHandleStatusChange}
                label={commonStrings.pickList}
                MenuProps={{
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  getContentAnchorEl: null
                }}
              >
                {status.map((item, index) => (
                  <MenuItem value={item.lov_name} key={index}>
                    {item.lov_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={6} sm={3} md={2} item>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="category-label">{commonStrings.categoryPayload}</InputLabel>
              <Select
                value={category || ''}
                onChange={handleChangecategory}
                label={commonStrings.categoryPayload}
                labelId="category-label"
                id="demo-simple-select"
                MenuProps={{
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  getContentAnchorEl: null
                }}
              >
                {categories.map((item, index) => (
                  <MenuItem value={item?.value} key={index}>
                    {item?.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={6} sm={4} md={7} item className={globalClasses.dfacjeg}>
            {isExportHidden && (
              <Tooltip title={commonStrings.ExportToExcel} placement="top" arrow>
                <span>
                  <Fab
                    onClick={handleExportData}
                    label={""}
                    iconAddExcel="excel"
                  />
                </span>
              </Tooltip>
            )}
          </Grid>
        </Grid>
        {pickListValue && category && (
          <QLIReportList
            ref={qliListRef}
            currentState={category}
            pickListValue={pickListValue}
            company={company}
          />
        )}
      </>
    </Container>
  );
};

export default QLIReports;
